// Event Hub client helpers — API paths, auth token storage, analytics tracking.
import api from "@/lib/api";

export const EVENT_HUB_BASE = "/event";

// ─── Volunteer PIN session token (separate from admin Google session) ────────
const VOL_TOKEN_KEY = "bs_volunteer_token";
const VOL_NAME_KEY = "bs_volunteer_name";
const CHECKED_IN_KEY = "bs_checked_in";

export function setVolunteerSession(token, name) {
  if (token) {
    localStorage.setItem(VOL_TOKEN_KEY, token);
    if (name) localStorage.setItem(VOL_NAME_KEY, name);
  } else {
    localStorage.removeItem(VOL_TOKEN_KEY);
    localStorage.removeItem(VOL_NAME_KEY);
  }
}

export function getVolunteerToken() {
  return localStorage.getItem(VOL_TOKEN_KEY) || "";
}

export function getVolunteerName() {
  return localStorage.getItem(VOL_NAME_KEY) || "";
}

export function clearVolunteerSession() {
  localStorage.removeItem(VOL_TOKEN_KEY);
  localStorage.removeItem(VOL_NAME_KEY);
}

// ─── Local check-in memory (so returning users skip check-in) ───────────────
export function markCheckedIn(payload) {
  try {
    localStorage.setItem(CHECKED_IN_KEY, JSON.stringify({ ...payload, ts: new Date().toISOString() }));
  } catch (e) { /* noop */ }
}

export function getCheckedIn() {
  try {
    const raw = localStorage.getItem(CHECKED_IN_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Expire after 48h so users re-verify on the next event
    if (parsed?.ts) {
      const age = Date.now() - new Date(parsed.ts).getTime();
      if (age > 48 * 3600 * 1000) return null;
    }
    return parsed;
  } catch { return null; }
}

export function clearCheckedIn() {
  localStorage.removeItem(CHECKED_IN_KEY);
}

// ─── Volunteer-authenticated API calls (attach x-volunteer-token) ───────────
export function volunteerApi() {
  const token = getVolunteerToken();
  return {
    get: (path, config = {}) => api.get(`${EVENT_HUB_BASE}${path}`, {
      ...config,
      headers: { ...(config.headers || {}), "x-volunteer-token": token },
    }),
    post: (path, body, config = {}) => api.post(`${EVENT_HUB_BASE}${path}`, body, {
      ...config,
      headers: { ...(config.headers || {}), "x-volunteer-token": token },
    }),
    patch: (path, body, config = {}) => api.patch(`${EVENT_HUB_BASE}${path}`, body, {
      ...config,
      headers: { ...(config.headers || {}), "x-volunteer-token": token },
    }),
    delete: (path, config = {}) => api.delete(`${EVENT_HUB_BASE}${path}`, {
      ...config,
      headers: { ...(config.headers || {}), "x-volunteer-token": token },
    }),
  };
}

// Public Event Hub endpoints (no auth)
export const eventApi = {
  config: () => api.get(`${EVENT_HUB_BASE}/config`),
  program: (event_day) => api.get(`${EVENT_HUB_BASE}/program`, { params: event_day ? { event_day } : {} }),
  announcements: () => api.get(`${EVENT_HUB_BASE}/announcements`),
  searchAttendees: (body) => api.post(`${EVENT_HUB_BASE}/checkin/search`, body),
  confirmCheckIn: (body) => api.post(`${EVENT_HUB_BASE}/checkin/confirm`, body),
  walkIn: (body) => api.post(`${EVENT_HUB_BASE}/checkin/walkin`, body),
  earlyAccess: (body) => api.post(`${EVENT_HUB_BASE}/early-access`, body),
  track: (kind, meta = {}) => {
    const sid = sessionId();
    return api.post(`${EVENT_HUB_BASE}/analytics/track`, { kind, session_id: sid, meta }).catch(() => {});
  },
  // V2 additions
  siteMode: () => api.get(`${EVENT_HUB_BASE}/site-mode`),
  listHosts: () => api.get(`${EVENT_HUB_BASE}/hosts`),
  groupCheckin: (body) => api.post(`${EVENT_HUB_BASE}/checkin/group`, body),
  profiles: (kind) => api.get(`${EVENT_HUB_BASE}/profiles`, { params: kind ? { kind } : {} }),
  profile: (slug) => api.get(`${EVENT_HUB_BASE}/profiles/${slug}`),
  profileClick: (slug, click_kind) => api.post(`${EVENT_HUB_BASE}/profiles/click`, { slug, click_kind }).catch(() => {}),
};

// Client-side session id (session-scoped, resets on tab close)
export function sessionId() {
  const key = "bs_analytics_sid";
  let sid = sessionStorage.getItem(key);
  if (!sid) {
    sid = `sid_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
    sessionStorage.setItem(key, sid);
  }
  return sid;
}

// ─── Event day helpers ──────────────────────────────────────────────────────
export const EVENT_DAY_FRIDAY = "2026-07-17";
export const EVENT_DAY_SATURDAY = "2026-07-18";

export function humanDay(day) {
  if (day === EVENT_DAY_FRIDAY) return "Friday · July 17";
  if (day === EVENT_DAY_SATURDAY) return "Saturday · July 18";
  if (day === "both") return "Both Days";
  return day;
}

export function humanTime(hhmm) {
  if (!hhmm || hhmm.length < 4) return hhmm || "";
  const [h, m] = hhmm.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  const hr = ((h + 11) % 12) + 1;
  return `${hr}:${String(m).padStart(2, "0")} ${period}`;
}
